import { PortfolioTotals, UserDataResult } from './types'
import {
  type AprWeightItem,
  calculateLeverage,
  calculateWeightedAprs,
} from './utils'

/**
 * Builds only portfolio totals - lightweight version without per-lender breakdown
 * Skips position extraction and lender summary objects for better performance
 *
 * @param userDataResult - The result from convertLenderUserDataResult or getLenderUserDataMulti
 * @returns PortfolioTotals with aggregated totals and weighted APRs
 *
 * @example
 * ```typescript
 * const userData = await getLenderUserDataMulti(account, chainQueries, lenderState)
 * const totals = buildPortfolioTotals(userData)
 *
 * console.log(`Net worth: $${totals.balanceData.nav.toFixed(2)}`)
 * console.log(`Net APR: ${totals.aprData.apr.toFixed(2)}%`)
 * ```
 */
export function buildPortfolioTotals(
  userDataResult: UserDataResult,
): PortfolioTotals {
  let totalDeposits = 0
  let totalDebt = 0
  let totalCollateral = 0
  let totalCollateralAllActive = 0
  let totalNav24h = 0
  let totalDeposits24h = 0
  let totalDebt24h = 0
  let lenderCount = 0

  const aprItems: AprWeightItem[] = []
  const activeChains = new Set<string>()

  for (const [chainId, lenders] of Object.entries(userDataResult)) {
    activeChains.add(chainId)

    for (const [, userData] of Object.entries(lenders)) {
      lenderCount++

      for (const subAccount of userData.data) {
        const { balanceData, aprData } = subAccount

        totalDeposits += balanceData.deposits
        totalDebt += balanceData.debt
        totalCollateral += balanceData.collateral
        totalCollateralAllActive += balanceData.collateralAllActive
        totalNav24h += balanceData.nav24h ?? 0
        totalDeposits24h += balanceData.deposits24h ?? 0
        totalDebt24h += balanceData.debt24h ?? 0

        // Collect APR data with balance weights for weighted averaging
        aprItems.push({
          deposits: balanceData.deposits,
          debt: balanceData.debt,
          nav: balanceData.deposits - balanceData.debt,
          apr: aprData.apr,
          depositApr: aprData.depositApr,
          borrowApr: aprData.borrowApr,
          rewardApr: aprData.rewardApr,
          rewardDepositApr: aprData.rewardDepositApr,
          rewardBorrowApr: aprData.rewardBorrowApr,
          intrinsicApr: aprData.intrinsicApr,
          intrinsicDepositApr: aprData.intrinsicDepositApr,
          intrinsicBorrowApr: aprData.intrinsicBorrowApr,
        })
      }
    }
  }

  const totalNav = totalDeposits - totalDebt
  const leverage = calculateLeverage(totalDeposits, totalNav)
  const weightedAprs = calculateWeightedAprs(aprItems)

  return {
    balanceData: {
      deposits: totalDeposits,
      debt: totalDebt,
      collateral: totalCollateral,
      collateralAllActive: totalCollateralAllActive,
      nav: totalNav,
      deposits24h: totalDeposits24h,
      debt24h: totalDebt24h,
      nav24h: totalNav24h,
    },
    aprData: {
      ...weightedAprs,
      rewards: {},
    },
    leverage,
    activeLenders: lenderCount,
    activeChains: activeChains.size,
  }
}
